import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { HelpCircle } from 'lucide-react';
import {
  getMetricEntry,
  type MetricKey,
} from '@/lib/metrics-glossary';

/**
 * Tooltip de ayuda para nomenclaturas técnicas (M2.32.E).
 *
 * Renderiza un span inline con la etiqueta original (children, `label`, o
 * `entry.short` por defecto) y un botón `?` minúsculo que despliega un
 * tooltip propio (CSS, sin Radix) con la definición completa del glosario:
 * nombre largo, definición, fórmula opcional y guía de interpretación.
 *
 * Decisiones:
 *  - Sin dependencias externas: usamos un `<div role="tooltip">` posicionado
 *    absolutamente respecto al wrapper. Hover, focus y Escape para abrir/cerrar.
 *  - El botón es focusable (`tabIndex` 0 implícito en `<button>`) — accesible
 *    por teclado: Tab para enfocar, Space/Enter para abrir, Escape para cerrar.
 *  - `aria-describedby` apunta al popover cuando está abierto, para que los
 *    lectores de pantalla anuncien el contenido.
 *  - Cierre automático al hacer click fuera o al perder el foco del wrapper.
 *  - El popover usa `pointerEvents: none` en la flecha para no interceptar
 *    hover y mantiene `aria-hidden` consistente con `open`.
 *  - El icono `HelpCircle` de lucide-react se sitúa con `align-text-top` para
 *    no romper la línea base de la etiqueta.
 */
export interface MetricTooltipProps {
  /** Clave del glosario; determina el contenido del popover. */
  metric: MetricKey;
  /**
   * Etiqueta visible inline (alternativa a children). Si no se pasa ni esto
   * ni children, se usa el `short` del glosario.
   */
  label?: string;
  /** Contenido inline custom (texto u otros elementos). */
  children?: ReactNode;
  /** Clases extra opcionales para el wrapper. */
  className?: string;
  /**
   * Permite forzar la posición vertical del popover. Por defecto se calcula
   * dinámicamente según el espacio disponible — `'top'` o `'bottom'`.
   */
  placement?: 'top' | 'bottom' | 'auto';
  /**
   * Si es `true`, no renderiza etiqueta inline — sólo el botón "?" con el
   * popover. Útil cuando la etiqueta ya existe fuera del tooltip (p.ej.
   * cuando el `<label>` HTML va separado del icono para preservar el binding
   * con `getByLabelText` en tests).
   */
  iconOnly?: boolean;
}

export function MetricTooltip({
  metric,
  label,
  children,
  className,
  placement = 'auto',
  iconOnly = false,
}: MetricTooltipProps): ReactElement {
  const entry = getMetricEntry(metric);
  const popoverId = useId();
  const wrapperRef = useRef<HTMLSpanElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  // Cuando cerramos vía Escape devolvemos el foco al botón, lo que dispara
  // onFocus → handleOpen y reabriría el popover. Esta ref sirve para suprimir
  // ese único focus programático sin romper la apertura normal por teclado.
  const suppressNextFocusRef = useRef(false);
  const [open, setOpen] = useState(false);
  // Posición resuelta cuando placement es 'auto'.
  const [resolvedPlacement, setResolvedPlacement] = useState<'top' | 'bottom'>(
    placement === 'bottom' ? 'bottom' : 'top',
  );

  // Etiqueta inline: prioridad children > label > short.
  const inlineLabel: ReactNode =
    children !== undefined && children !== null && children !== ''
      ? children
      : (label ?? entry.short);

  // Decide posición top/bottom al abrir (si placement = 'auto').
  const computePlacement = useCallback(() => {
    if (placement !== 'auto') {
      setResolvedPlacement(placement);
      return;
    }
    const el = wrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    // Si hay menos de 180px sobre el wrapper, abrimos hacia abajo.
    setResolvedPlacement(rect.top < 180 && viewportH - rect.bottom > 180 ? 'bottom' : 'top');
  }, [placement]);

  // Click outside / Escape cierran.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        // Marca para que el siguiente focus() programático no reabra el popover.
        suppressNextFocusRef.current = true;
        setOpen(false);
        // Devolvemos el foco al botón para no perderlo en el aire.
        buttonRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function handleOpen() {
    computePlacement();
    setOpen(true);
  }

  function handleToggle() {
    if (open) {
      setOpen(false);
    } else {
      handleOpen();
    }
  }

  // Cierre al perder el foco completo del wrapper.
  function handleBlur(e: React.FocusEvent<HTMLSpanElement>) {
    if (!wrapperRef.current) return;
    const next = e.relatedTarget as Node | null;
    if (next && wrapperRef.current.contains(next)) return;
    setOpen(false);
  }

  return (
    <span
      ref={wrapperRef}
      data-testid={`metric-tooltip-${metric}`}
      className={`relative inline-flex items-center gap-0.5 align-baseline ${className ?? ''}`}
      onMouseEnter={handleOpen}
      onMouseLeave={() => setOpen(false)}
      onBlur={handleBlur}
    >
      {!iconOnly && (
        <span data-testid={`metric-tooltip-label-${metric}`}>{inlineLabel}</span>
      )}
      <button
        ref={buttonRef}
        type="button"
        aria-label={`Ayuda: ${entry.fullName}`}
        aria-describedby={open ? popoverId : undefined}
        aria-expanded={open}
        onClick={handleToggle}
        onFocus={() => {
          if (suppressNextFocusRef.current) {
            suppressNextFocusRef.current = false;
            return;
          }
          handleOpen();
        }}
        data-testid={`metric-tooltip-button-${metric}`}
        className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full text-slate-400 transition hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
      >
        <HelpCircle aria-hidden="true" className="h-3.5 w-3.5" />
      </button>
      {open && (
        <span
          id={popoverId}
          role="tooltip"
          data-testid={`metric-tooltip-popover-${metric}`}
          data-placement={resolvedPlacement}
          className={`absolute left-0 z-50 w-[280px] max-w-[calc(100vw-1rem)] rounded-lg border border-slate-700/40 bg-slate-800/95 p-3 text-left text-xs leading-relaxed text-slate-50 shadow-xl ${
            resolvedPlacement === 'top'
              ? 'bottom-full mb-2'
              : 'top-full mt-2'
          }`}
        >
          <strong className="block text-[13px] font-semibold text-white">
            {entry.fullName}{' '}
            <span className="font-normal text-slate-300">({entry.short})</span>
          </strong>
          <p className="mt-1 text-slate-100">{entry.definition}</p>
          {entry.formula && (
            <code
              data-testid={`metric-tooltip-formula-${metric}`}
              className="mt-2 block whitespace-pre-wrap break-words rounded bg-black/40 px-2 py-1 font-mono text-[11px] text-amber-200"
            >
              {entry.formula}
            </code>
          )}
          <p className="mt-2 text-slate-200">
            <span className="font-semibold text-slate-50">Interpretación: </span>
            {entry.interpretation}
          </p>
        </span>
      )}
    </span>
  );
}
